import { getAccessToken, getScopeHeaders } from '../lib/auth-storage';

const API_BASE = import.meta.env.VITE_PRODUCT_ATTRIBUTES_API ?? '/api/v1';

export type AttributeType =
  | 'text' | 'number' | 'boolean' | 'select' | 'multiselect' | 'date' | 'url' | 'json'
  | 'measurement' | 'compliance' | 'seo' | 'ai' | 'marketplace';

export type Attribute = {
  id: string;
  tenant_id: string;
  organization_id: string;
  code: string;
  name: string;
  description?: string;
  type: AttributeType;
  status: string;
  scope?: string;
  required?: boolean;
  searchable?: boolean;
  filterable?: boolean;
  options?: Array<{ id?: string; label: string; value: string; order?: number }>;
  rules?: Array<{ id?: string; type: string; value?: string; message?: string }>;
  metadata?: Record<string, string>;
  version: number;
  created_at: string;
  updated_at: string;
};

export type AttributeSet = {
  id: string;
  name: string;
  code: string;
  description?: string;
  status: string;
  items?: Array<{ id?: string; attribute_id: string; required?: boolean; sort_order?: number }>;
  created_at: string;
  updated_at: string;
};

export type AttributeTemplate = {
  id: string;
  name: string;
  code: string;
  description?: string;
  attribute_ids?: string[];
  created_at: string;
  updated_at: string;
};

export type Specification = {
  id: string;
  product_id: string;
  name: string;
  description?: string;
  groups?: Array<{ id?: string; name: string; order?: number; items?: Array<{ label: string; value: string; unit?: string }> }>;
  created_at: string;
  updated_at: string;
};

export type MarketplaceAttributeMapping = {
  id: string;
  attribute_id: string;
  marketplace: string;
  external_attribute_id: string;
  external_name?: string;
  created_at: string;
  updated_at: string;
};

export type ValidationResult = {
  valid: boolean;
  score: number;
  errors?: Array<{ attribute_id?: string; code: string; message: string }>;
  warnings?: Array<{ attribute_id?: string; code: string; message: string }>;
};

export type AttributeRecommendation = {
  attribute_id: string;
  attribute_name: string;
  code: string;
  score: number;
  confidence: number;
  reason?: string;
};

export type AttributesDashboard = {
  stats?: Record<string, unknown>;
  widgets?: Array<{ id: string; title: string; type: string }>;
};

function headers(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...getScopeHeaders(),
    ...(getAccessToken() ? { Authorization: `Bearer ${getAccessToken()}` } : {}),
  };
}

async function attrFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { ...headers(), ...(init?.headers as Record<string, string> | undefined) },
  });
  if (!res.ok) throw new Error(`Attributes ${path} failed: ${res.status} ${await res.text()}`);
  const body = await res.json();
  return (body.data ?? body) as T;
}

export const productAttributesApi = {
  dashboard: () => attrFetch<AttributesDashboard>('/product-attributes/dashboard'),

  listAttributes: (params?: { q?: string; type?: string; status?: string }) => {
    const qs = new URLSearchParams();
    if (params?.q) qs.set('q', params.q);
    if (params?.type) qs.set('type', params.type);
    if (params?.status) qs.set('status', params.status);
    const q = qs.toString();
    return attrFetch<Attribute[]>(`/attributes${q ? `?${q}` : ''}`);
  },
  getAttribute: (id: string) => attrFetch<Attribute>(`/attributes/${id}`),
  createAttribute: (input: { code: string; name: string; type: AttributeType; description?: string; required?: boolean; scope?: string }) =>
    attrFetch<Attribute>('/attributes', { method: 'POST', body: JSON.stringify(input) }),
  updateAttribute: (id: string, input: Partial<Attribute>) =>
    attrFetch<Attribute>(`/attributes/${id}`, { method: 'PUT', body: JSON.stringify(input) }),
  deleteAttribute: (id: string) => attrFetch<void>(`/attributes/${id}`, { method: 'DELETE' }),
  validate: (input: { product_id: string; values: Record<string, string> }) =>
    attrFetch<ValidationResult>('/attributes/validate', { method: 'POST', body: JSON.stringify(input) }),
  recommend: (input: { product_id: string; product_name: string; category?: string; limit?: number }) =>
    attrFetch<AttributeRecommendation[]>('/attributes/recommend', { method: 'POST', body: JSON.stringify(input) }),

  listAttributeSets: () => attrFetch<AttributeSet[]>('/attribute-sets'),
  getAttributeSet: (id: string) => attrFetch<AttributeSet>(`/attribute-sets/${id}`),
  createAttributeSet: (input: { code: string; name: string; description?: string; items?: AttributeSet['items'] }) =>
    attrFetch<AttributeSet>('/attribute-sets', { method: 'POST', body: JSON.stringify(input) }),
  updateAttributeSet: (id: string, input: Partial<AttributeSet>) =>
    attrFetch<AttributeSet>(`/attribute-sets/${id}`, { method: 'PUT', body: JSON.stringify(input) }),

  listTemplates: () => attrFetch<AttributeTemplate[]>('/attribute-templates'),
  createTemplate: (input: { code: string; name: string; description?: string; attribute_ids?: string[] }) =>
    attrFetch<AttributeTemplate>('/attribute-templates', { method: 'POST', body: JSON.stringify(input) }),

  listSpecifications: (params?: { product_id?: string }) => {
    const qs = params?.product_id ? `?product_id=${encodeURIComponent(params.product_id)}` : '';
    return attrFetch<Specification[]>(`/specifications${qs}`);
  },
  createSpecification: (input: { product_id: string; name: string; description?: string; groups?: Specification['groups'] }) =>
    attrFetch<Specification>('/specifications', { method: 'POST', body: JSON.stringify(input) }),

  listMarketplaceMappings: (params?: { marketplace?: string; attribute_id?: string }) => {
    const qs = new URLSearchParams();
    if (params?.marketplace) qs.set('marketplace', params.marketplace);
    if (params?.attribute_id) qs.set('attribute_id', params.attribute_id);
    const q = qs.toString();
    return attrFetch<MarketplaceAttributeMapping[]>(`/marketplace-attributes${q ? `?${q}` : ''}`);
  },
  mapMarketplaceAttribute: (input: { attribute_id: string; marketplace: string; external_attribute_id: string; external_name?: string }) =>
    attrFetch<MarketplaceAttributeMapping>('/marketplace-attributes/map', { method: 'POST', body: JSON.stringify(input) }),
};
